import { ScreenDimensions, TileSize } from "game-common/models"
import { Container, Graphics, Rectangle, Text } from "pixi.js"

import { BUTTON_SIZES } from "./ui_tokens"
import { TextRole } from "../text/typography"
import { createButton } from "./button_factory"
import { TextRenderer } from "../text/text_renderer"

/**
 * The two ends of a possession, announced.
 *
 * A visit starts and ends by things happening to you somewhere else -- a card you pressed a second
 * ago, a defender's axe, a camp whose player closed their tab -- and before this the only signal was
 * that the world abruptly looked different: you were a skeleton outside a stranger's palisade, or
 * you were suddenly standing in a camp of your own. Both transitions now stop and say so, in the
 * same voice the end-of-run scorecard uses.
 *
 * NO NEW WIRE. Both hang off `Message.invading`, the verdict the client is already sent every
 * message for its own entity (it is what draws "Let go" and what dismisses the scorecard), so the
 * two splashes are simply its two TRANSITIONS -- see GuiManager.setInvading. Nothing has to be
 * announced separately, and nothing can announce a visit the server does not believe in.
 *
 * Deliberately simpler than the scorecard: one line, one way out, no numbers. The scorecard is a
 * reckoning and earns its flourish; these are doorways.
 */

export type PossessionSplashKind = "possessed" | "returned"

export interface PossessionSplashCopy {
    title: string
    line: string
    button: string
}

/**
 * What each splash says. Pure, exported and tested, so the wording can be read and changed without
 * touching the drawing -- and so a kind nobody has written copy for cannot ship as a blank panel.
 */
export const possessionSplashCopy = (kind: PossessionSplashKind): PossessionSplashCopy =>
    kind === "possessed"
        ? {
              title: "YOU HAVE POSSESSED AN INVADER",
              line: "You wear one of the wave's attackers. The camp does not know you.",
              button: "DISMISS",
          }
        : {
              title: "RETURNED TO THE LAND OF THE LIVING",
              line: "The borrowed body is finished with. You are yourself again.",
              button: "DISMISS",
          }

/**
 * Which splash a change in the invading verdict calls for, if any.
 *
 * Pure, because the decision is the interesting part and GuiManager -- where it is acted on -- cannot
 * be instantiated under jest. `Message.invading` arrives on EVERY message, so the rule has to be
 * "only on a change": acting on the value itself would re-announce the same visit several times a
 * second.
 */
export const splashForInvadingChange = (was: boolean, now: boolean): PossessionSplashKind | undefined =>
    was === now ? undefined : now ? "possessed" : "returned"

export interface PossessionSplashProps {
    onDismiss?: () => void
}

// ===== geometry =======================================================================
// Stage units, with the 1.3 vertical stretch already accounted for -- the frame run_summary.ts and
// invasion_picker.ts work in, because this opens over the same screen.
const SCREEN_W = ScreenDimensions.w + TileSize * 4.7
const CENTER_X = SCREEN_W / 2
const CENTER_Y = ScreenDimensions.h / 2 + 52

const PANEL_W = 420
const PANEL_H = 150
const PANEL_LEFT = CENTER_X - PANEL_W / 2
const PANEL_TOP = CENTER_Y - PANEL_H / 2

const BUTTON_W = 150
const BUTTON_H = BUTTON_SIZES.medium.height
const BUTTON_TOP = PANEL_TOP + PANEL_H - BUTTON_H - 18

// Palette, shared with run_summary.ts / invasion_picker.ts.
const PANEL_FILL = 0x1b1512
const PANEL_ALPHA = 0.97
const RIM = 0xf2a94e
const TEXT_COLOR = 0xfff3e0
const MUTED = 0xbfae99
const SCRIM_COLOR = 0x06060a
// The undead's own cold colour, for the half of this that is about wearing somebody else.
const UNDEAD = 0x9fd2ff

const FADE_STEP = 0.14

export class PossessionSplash extends Container {
    private onDismiss?: () => void
    private scrim: Graphics
    private panel: Container
    private content?: Container
    private opened = false
    private kind: PossessionSplashKind = "possessed"

    constructor(props: PossessionSplashProps = {}) {
        super()
        this.onDismiss = props.onDismiss

        // The scrim swallows every press that is not the button, and unlike the picker's it is NOT
        // an answer: this panel is telling the player something, and a stray click while they are
        // reading it should not fire their bow into the camp behind -- nor dismiss a message they
        // have not read.
        this.scrim = new Graphics()
        this.scrim.beginFill(SCRIM_COLOR, 0.55)
        this.scrim.drawRect(-400, -400, SCREEN_W + 800, 1400)
        this.scrim.endFill()
        this.scrim.interactive = true
        this.scrim.hitArea = new Rectangle(-400, -400, SCREEN_W + 800, 1400)
        this.scrim.on("pointerdown", (event: any) => event.stopPropagation())
        this.scrim.on("pointermove", (event: any) => event.stopPropagation())
        this.scrim.on("rightclick", (event: any) => event.stopPropagation())
        this.addChild(this.scrim)

        this.panel = new Container()
        this.panel.interactive = true
        this.panel.hitArea = new Rectangle(PANEL_LEFT, PANEL_TOP, PANEL_W, PANEL_H)
        this.panel.on("pointerdown", (event: any) => event.stopPropagation())
        this.panel.on("pointermove", (event: any) => event.stopPropagation())
        this.addChild(this.panel)

        this.visible = false
        this.alpha = 0
    }

    public isOpen = (): boolean => this.opened

    /** Which message is up, for a caller outside the display tree (and for a test). */
    public shownKind = (): PossessionSplashKind | undefined => (this.opened ? this.kind : undefined)

    public open = (kind: PossessionSplashKind) => {
        this.kind = kind
        this.build()
        if (!this.visible) {
            this.alpha = 0
        }
        this.opened = true
        this.visible = true
    }

    public close = () => {
        this.opened = false
    }

    public animate = (_dtMs: number) => {
        if (!this.visible) {
            return
        }
        const target = this.opened ? 1 : 0
        if (this.alpha === target) {
            return
        }
        this.alpha = target > this.alpha ? Math.min(1, this.alpha + FADE_STEP) : Math.max(0, this.alpha - FADE_STEP)
        if (this.alpha <= 0) {
            this.visible = false
        }
    }

    // ===== internals ==================================================================

    private build = () => {
        if (this.content) {
            this.panel.removeChild(this.content)
            this.content.destroy({ children: true })
            this.content = undefined
        }
        const copy = possessionSplashCopy(this.kind)
        // Warm for coming home, cold for putting somebody else's face on -- the one accent the
        // scorecard and the picker already use to mean "this is the undead's half".
        const accent = this.kind === "possessed" ? UNDEAD : RIM

        const content = new Container()
        this.content = content
        this.panel.addChild(content)

        const chrome = new Graphics()
        chrome.beginFill(accent, 0.1)
        chrome.drawRoundedRect(PANEL_LEFT - 6, PANEL_TOP - 6, PANEL_W + 12, PANEL_H + 12, 16)
        chrome.endFill()
        chrome.lineStyle(1.5, accent, 0.85)
        chrome.beginFill(PANEL_FILL, PANEL_ALPHA)
        chrome.drawRoundedRect(PANEL_LEFT, PANEL_TOP, PANEL_W, PANEL_H, 12)
        chrome.endFill()
        content.addChild(chrome)

        const title = this.text(copy.title, { role: "compactTitle", color: accent, bold: true })
        title.x = Math.round(CENTER_X - title.width / 2)
        title.y = PANEL_TOP + 22
        content.addChild(title)

        const line = this.text(copy.line, { role: "compactLabel", color: MUTED })
        line.x = Math.round(CENTER_X - line.width / 2)
        line.y = PANEL_TOP + 58
        content.addChild(line)

        const button = createButton({ label: copy.button, theme: "ember", preset: "primary", appearance: "outline",
            width: BUTTON_W, height: BUTTON_H, size: "medium", bold: true,
            accent: this.kind === "possessed" ? UNDEAD : RIM, onClick: this.dismiss })
        button.position.set(CENTER_X - BUTTON_W / 2, BUTTON_TOP)
        content.addChild(button)
    }

    /** Guarded on `opened`, so the fade-out cannot answer a second time on the way off the screen. */
    private dismiss = () => {
        if (!this.opened) {
            return
        }
        this.close()
        this.onDismiss?.()
    }

    private text = (
        value: string,
        opts: { role: TextRole; color?: number; bold?: boolean },
    ): Text => {
        let renderer = new TextRenderer().role(opts.role).bold(false).align("center")
        if (opts.color !== undefined) {
            renderer = renderer.color(opts.color)
        }
        if (opts.bold) {
            renderer = renderer.bold()
        }
        return renderer.render(value) as Text
    }
}
